import {
  Box,
  Button,
  Center,
  Grid,
  HStack,
  IconButton,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { DataTable } from '@gamma/ui/data-table';
import RefreshSharpIcon from '@gamma/ui/icons/RefreshSharpIcon';
import { FlushPanelContent, Panel } from '@gamma/ui/layout';
import { CrowdStrikeMark } from '@gamma/ui/logos';
import { GraphQLReqStatus, LoadingSpinner } from '@gamma/ui/progress';
import moment from 'moment';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Column as TableColumn } from 'react-table';
import {
  useDateRangeQueryString,
  useManualPagination,
} from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { MISSING_DATA_PLACEHOLDER } from '../../../../../shared/constants';
import { useEntitiesContext, useEntityEdrContext } from '../../../context';
import {
  EntityEdrStatus,
  NICHistory,
  useGetNicHistoryForEndpointDeviceEntityQuery,
  useGetUserHistoryForEndpointDeviceEntityQuery,
} from '../../../queries';
import {
  UserHistory,
  EntityDataStoreEntityType,
} from '@investigator/shared/generated/graphql';
import { ChangeStatusModal } from '../ChangeStatusModal/ChangeStatusModal';
import { formatDateRangeDisplay } from '@investigator/shared/utils';

const { entityEDR } = i18n.pages.detections;
const { headings, edrAction } = entityEDR;
const { interfaceHistory } = headings;

const dataTableInterfaceColumns: TableColumn<NICHistory>[] = [
  {
    Header: entityEDR.tableHeaders.ipAddress,
    accessor: 'ip_address',
    disableSortBy: true,
  },
  {
    Header: entityEDR.macAddress,
    accessor: 'mac_address',
    disableSortBy: true,
  },
  {
    Header: entityEDR.timestamp,
    accessor: 'obsts',
    Cell: (props) => {
      return <>{moment.unix(props.value).format('MMM D, h:mma')}</>;
    },
  },
];

export const CrowdstrikeModalSection = (): JSX.Element => {
  const { selectedEntity } = useEntitiesContext();
  const userTableRef = useRef<HTMLDivElement>(null);
  const nicTableRef = useRef<HTMLDivElement>(null);
  const {
    crowdstrikeStatus,
    performEdrAction,
    performEdrActionLoading,
    edrData,
  } = useEntityEdrContext();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { userRole, userPrivileges } = useContext(AuthContext);
  const { start, end } = useDateRangeQueryString();

  // Get crowdstrike data from context - parent components ensure this exists
  const crowdstrikeData = edrData?.crowdstrike;
  const data = crowdstrikeData;
  const { tenantEntity, tenantInfo, lastSeen, entityName, detection_id } =
    selectedEntity || {};
  const tenant_id = tenantInfo?.tenant_id ?? '';

  const selectedEntityStart = selectedEntity?.start ?? 0;
  const selectedEntityEnd = selectedEntity?.end ?? 0;
  // Calculate date range display from actual timestamps being used
  const dateRangeDisplay = formatDateRangeDisplay(
    selectedEntityStart,
    selectedEntityEnd,
  );

  const edrActionEnabled =
    userPrivileges?.[tenant_id]?.crowdstrike_action_enabled;

  const { entity_id, device_id, host_info, last_updated_timestamp } =
    data || {};

  const {
    mac_address,
    os,
    ad_domain_name,
    platform,
    external_ip,
    sensor_version,
    cloud_account_id,
    cloud_instance_id,
    cloud_provider,
    local_ip: _local_ip,
    host_name,
    platform_type,
  } = host_info ?? {};

  // Get status from context
  const { entity_edr_status } = crowdstrikeStatus.data || {};
  const getEntityEdrStatusLoading = crowdstrikeStatus.loading ?? true;
  const pullEntityEdrStatusLoading = crowdstrikeStatus.refreshLoading ?? true;

  const edrStatusAlert = useMemo(() => {
    if (entity_edr_status) {
      switch (entity_edr_status) {
        case EntityEdrStatus.Normal:
          return {
            colorScheme: 'green',
            statusText: edrAction.normal,
          };
        case EntityEdrStatus.Isolated:
          return {
            colorScheme: 'red',
            statusText: edrAction.isolated,
          };
        case EntityEdrStatus.IsolationPending:
          return {
            colorScheme: 'yellow',
            statusText: edrAction.pendingIsolation,
          };
        case EntityEdrStatus.LiftIsolationPending:
          return {
            colorScheme: 'yellow',
            statusText: edrAction.pendingLift,
          };
      }
    }
    return undefined;
  }, [entity_edr_status]);

  const [nicInitialFetch, nicSetInitialFetch] = useState<boolean>(false);

  const {
    sortBy: nicSortBy,
    offset: nicOffset,
    pageSize: nicPageSize,
    pageIndex: nicPageIndex,
    pageCount: nicPageCount,
    onFetchData: nicOnFetchData,
    getPageCount: nicGetPageCount,
    getSortsForAPI: nicGetSortsForAPI,
  } = useManualPagination<NICHistory>({
    initialFetch: nicInitialFetch,
    sortBy: [{ id: 'obsts', desc: true }],
  });

  const {
    loading: getNicHistoryLoading,
    data: getNicHistoryData,
    error: _getNicHistoryError,
  } = useGetNicHistoryForEndpointDeviceEntityQuery({
    skip: !lastSeen || !entity_id,
    variables: {
      as_of: lastSeen,
      entity: {
        entity_id,
        entity_type: EntityDataStoreEntityType.EndpointDevice,
      },
      tenant: tenant_id,
      offset: nicOffset,
      size: nicPageSize,
      sort: nicGetSortsForAPI(nicSortBy),
      use_cache: true,
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: ({ getNicHistoryForEndpointDeviceEntity }) => {
      nicSetInitialFetch(false);
      nicGetPageCount(getNicHistoryForEndpointDeviceEntity);
    },
  });

  const [userInitialFetch, userSetInitialFetch] = useState<boolean>(false);

  const {
    sortBy: userSortBy,
    offset: userOffset,
    pageSize: userPageSize,
    pageIndex: userPageIndex,
    getPageCount: userGetPageCount,
    getSortsForAPI: userGetSortsForAPI,
  } = useManualPagination<UserHistory>({
    initialFetch: userInitialFetch,
    sortBy: [{ id: 'obsts', desc: true }],
  });

  const { error: _getUserHistoryError } =
    useGetUserHistoryForEndpointDeviceEntityQuery({
      skip: !lastSeen || !entity_id,
      variables: {
        tenant: tenant_id,
        as_of: lastSeen,
        entity: {
          entity_id,
          entity_type: EntityDataStoreEntityType.EndpointDevice,
        },
        offset: userOffset,
        size: userPageSize,
        sort: userGetSortsForAPI(userSortBy),
        use_cache: true,
      },
      fetchPolicy: 'cache-and-network',
      onCompleted: ({ getUserHistoryForEndpointDeviceEntity }) => {
        userSetInitialFetch(false);
        userGetPageCount(getUserHistoryForEndpointDeviceEntity);
      },
    });

  // Handle refresh using context
  const handleRefreshStatus = useCallback(() => {
    if (tenantInfo && tenantEntity) {
      crowdstrikeStatus.refresh();
    }
  }, [tenantInfo, tenantEntity, crowdstrikeStatus]);

  // Handle status change action
  const handleStatusChange = useCallback(async () => {
    const action =
      entity_edr_status === EntityEdrStatus.Isolated
        ? 'lift_isolation'
        : 'isolate';
    await performEdrAction({
      entityId: entity_id ?? '',
      action,
      tenant: tenant_id ?? '',
      detectionId: detection_id,
    });
    onClose();
  }, [
    entity_edr_status,
    performEdrAction,
    entity_id,
    tenant_id,
    detection_id,
    onClose,
  ]);

  useEffect(() => {
    nicSetInitialFetch(true);
    userSetInitialFetch(true);
  }, [start, end]);

  // Scroll User History table back into view when page changes
  useEffect(() => {
    if (userPageIndex > 0 && userTableRef.current) {
      userTableRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [userPageIndex]);

  // Scroll Interface History table back into view when page changes
  useEffect(() => {
    if (nicPageIndex > 0 && nicTableRef.current) {
      nicTableRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [nicPageIndex]);

  // Calculate items for pagination display
  const nicTotalItems =
    getNicHistoryData?.getNicHistoryForEndpointDeviceEntity?.total_items || 0;

  const nicPaginationProps = {
    itemCount: nicTotalItems,
    pageCount: nicPageCount,
    data: (
      getNicHistoryData?.getNicHistoryForEndpointDeviceEntity?.nic_histories ||
      []
    ).filter(Boolean) as NICHistory[],
    isPaginable: nicTotalItems > 0,
    isLoading: getNicHistoryLoading,
  };

  if (!crowdstrikeData) {
    return (
      <VStack w="100%" alignItems="center" py={6}>
        <LoadingSpinner />
      </VStack>
    );
  }

  return (
    <VStack w="100%" alignItems="start" spacing={3} p={'8px'}>
      <HStack data-testid="crowdstrike-header">
        <CrowdStrikeMark height={'20px'} width={'20px'} />
        <Text textStyle="h5">CrowdStrike</Text>
        {dateRangeDisplay && (
          <Text color="gray.400" fontWeight="normal">
            {dateRangeDisplay}
          </Text>
        )}
      </HStack>

      {/* Host Information Grid */}
      <VStack w="100%" alignItems="start" spacing={3}>
        <Text fontWeight="semibold" fontSize="md">
          Host Information
        </Text>
        <Grid
          templateColumns="repeat(8, 1fr)"
          rowGap={4}
          columnGap={4}
          w="100%"
        >
          {/* Row 1 */}
          <VStack alignItems="start" spacing={1} minW="max-content">
            <Text color="gray.400" fontSize="md">
              {edrAction.entityStatus}
            </Text>
            <Box w="100%">
              {getEntityEdrStatusLoading ? (
                <GraphQLReqStatus
                  loading={getEntityEdrStatusLoading}
                  spinnerSize="xs"
                />
              ) : edrStatusAlert ? (
                <HStack spacing={2} alignItems="center">
                  <Tag
                    size="sm"
                    variant="solid"
                    px={2}
                    color="white"
                    borderRadius="20px"
                    textAlign="center"
                    bg={
                      edrStatusAlert?.colorScheme === 'red'
                        ? '#E53E3E'
                        : edrStatusAlert?.colorScheme === 'green'
                          ? 'green.500'
                          : edrStatusAlert?.colorScheme === 'yellow'
                            ? 'yellow.500'
                            : undefined
                    }
                    fontSize="11px"
                    fontWeight="normal"
                    whiteSpace="nowrap"
                    h="24px"
                  >
                    {edrStatusAlert?.statusText}
                  </Tag>
                  <IconButton
                    size="xs"
                    aria-label={edrAction.refreshEntityStatus}
                    onClick={handleRefreshStatus}
                    isDisabled={
                      pullEntityEdrStatusLoading || userRole === 'viewer'
                    }
                    icon={
                      pullEntityEdrStatusLoading ? (
                        <Spinner size="xs" />
                      ) : (
                        <RefreshSharpIcon fontSize="10px" />
                      )
                    }
                    variant="ghost"
                    color="whiteAlpha.800"
                    _hover={{ bg: 'whiteAlpha.200' }}
                  />
                  <Tooltip
                    label={
                      userRole !== 'admin'
                        ? edrAction.requestPermissions
                        : undefined
                    }
                  >
                    <Button
                      size="xs"
                      onClick={onOpen}
                      isDisabled={!edrActionEnabled || userRole !== 'admin'}
                      variant="outline"
                      borderColor="border.1"
                      bg="layer.2"
                      color="text.primary"
                      _hover={{
                        bg: 'layer.3',
                        borderColor: 'border.2',
                      }}
                      px={3}
                      h="24px"
                      fontSize="11px"
                      whiteSpace="nowrap"
                      borderRadius="full"
                      fontWeight="medium"
                    >
                      {entity_edr_status === EntityEdrStatus.Isolated
                        ? edrAction.liftIsolation
                        : edrAction.isolateEntity}
                    </Button>
                  </Tooltip>
                  <ChangeStatusModal
                    isOpen={isOpen}
                    onClose={onClose}
                    detectionId={detection_id ?? ''}
                    tenant={tenant_id ?? ''}
                    action={
                      entity_edr_status === EntityEdrStatus.Isolated
                        ? 'lift_isolation'
                        : 'isolate'
                    }
                    platform="CrowdStrike"
                    entityId={entity_id ?? ''}
                    entityName={entityName}
                    onConfirm={handleStatusChange}
                    isLoading={performEdrActionLoading}
                  />
                </HStack>
              ) : (
                <Text
                  fontSize="sm"
                  data-testid="crowdstrike-entity-status-value"
                >
                  {MISSING_DATA_PLACEHOLDER}
                </Text>
              )}
            </Box>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" fontSize="md">
              {entityEDR.timestamp}
            </Text>
            {(() => {
              const formattedTime = last_updated_timestamp
                ? moment
                    .unix(last_updated_timestamp)
                    .format('MMM D, YYYY h:mma')
                : '-';
              return (
                <Tooltip label={formattedTime} placement="top" hasArrow>
                  <Text noOfLines={1} fontSize="md" w="100%">
                    {formattedTime}
                  </Text>
                </Tooltip>
              );
            })()}
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" fontSize="md">
              {entityEDR.macAddress}
            </Text>
            <Tooltip label={mac_address || '-'} placement="top" hasArrow>
              <Text noOfLines={1} fontSize="md" w="100%">
                {mac_address || '-'}
              </Text>
            </Tooltip>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" fontSize="md">
              {entityEDR.hostName}
            </Text>
            <Tooltip label={host_name || '-'} placement="top" hasArrow>
              <Text noOfLines={1} fontSize="md" w="100%">
                {host_name || '-'}
              </Text>
            </Tooltip>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" fontSize="md">
              {entityEDR.platform}
            </Text>
            <Tooltip label={platform || 'Server'} placement="top" hasArrow>
              <Text noOfLines={1} fontSize="md" w="100%">
                {platform || 'Server'}
              </Text>
            </Tooltip>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" fontSize="md">
              {entityEDR.platformType}
            </Text>
            <Tooltip label={platform_type || 'Server'} placement="top" hasArrow>
              <Text noOfLines={1} fontSize="md" w="100%">
                {platform_type || 'Server'}
              </Text>
            </Tooltip>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" fontSize="md">
              {entityEDR.os}
            </Text>
            <Text fontSize="md" w="100%" title={os || undefined}>
              {os || '-'}
            </Text>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" fontSize="md">
              {entityEDR.externalIp}
            </Text>
            <Tooltip label={external_ip || '-'} placement="top" hasArrow>
              <Text noOfLines={1} fontSize="md" w="100%">
                {external_ip || '-'}
              </Text>
            </Tooltip>
          </VStack>

          {/* Row 2 */}
          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" noOfLines={1} fontSize="md">
              {entityEDR.deviceId}
            </Text>
            <Tooltip label={device_id || '-'} placement="top" hasArrow>
              <Text noOfLines={1} fontSize="md" w="100%">
                {device_id || '-'}
              </Text>
            </Tooltip>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" noOfLines={1} fontSize="md">
              {entityEDR.sensorVersion}
            </Text>
            <Tooltip label={sensor_version || '-'} placement="top" hasArrow>
              <Text noOfLines={1} fontSize="md" w="100%">
                {sensor_version || '-'}
              </Text>
            </Tooltip>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" noOfLines={1} fontSize="md">
              {entityEDR.cloudAccountId}
            </Text>
            <Tooltip label={cloud_account_id || '-'} placement="top" hasArrow>
              <Text noOfLines={1} fontSize="md" w="100%">
                {cloud_account_id || '-'}
              </Text>
            </Tooltip>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" noOfLines={1} fontSize="md">
              {entityEDR.cloudInstanceId}
            </Text>
            <Tooltip label={cloud_instance_id || '-'} placement="top" hasArrow>
              <Text noOfLines={1} fontSize="md" w="100%">
                {cloud_instance_id || '-'}
              </Text>
            </Tooltip>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" noOfLines={1} fontSize="md">
              {entityEDR.cloudProvider}
            </Text>
            <Tooltip label={cloud_provider || '-'} placement="top" hasArrow>
              <Text noOfLines={1} fontSize="md" w="100%">
                {cloud_provider || '-'}
              </Text>
            </Tooltip>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Text color="gray.400" noOfLines={1} fontSize="md">
              {entityEDR.adDomainName}
            </Text>
            <Tooltip label={ad_domain_name || '-'} placement="top" hasArrow>
              <Text noOfLines={1} fontSize="md" w="100%">
                {ad_domain_name || '-'}
              </Text>
            </Tooltip>
          </VStack>

          <VStack alignItems="start" spacing={1} minW={0}>
            <Tooltip
              label={entityEDR.hostBasedIdentityAlerts}
              placement="top"
              hasArrow
            >
              <Text color="gray.400" noOfLines={1} fontSize="md">
                {entityEDR.hostBasedIdentityAlerts}
              </Text>
            </Tooltip>
            <Text noOfLines={1} fontSize="md" w="100%">
              {'-'} {/*TODO: Value is not present in the data*/}
            </Text>
          </VStack>
        </Grid>
      </VStack>

      {/* Interface History Table */}
      {getNicHistoryLoading && nicTotalItems === 0 ? (
        <VStack ref={nicTableRef} w="100%" alignItems="start" spacing={3}>
          <Text
            fontFamily="'IBM Plex Sans', sans-serif"
            fontSize="14px"
            fontWeight="600"
            lineHeight="20px"
            letterSpacing="0.16px"
            color="#F4F4F4"
          >
            {interfaceHistory}
          </Text>
          <Panel w="100%">
            <Center py={8}>
              <LoadingSpinner />
            </Center>
          </Panel>
        </VStack>
      ) : (
        <VStack ref={nicTableRef} w="100%" alignItems="start" spacing={3}>
          <Text
            fontFamily="'IBM Plex Sans', sans-serif"
            fontSize="14px"
            fontWeight="600"
            lineHeight="20px"
            letterSpacing="0.16px"
            color="#F4F4F4"
          >
            {interfaceHistory}
          </Text>
          <Panel>
            <FlushPanelContent>
              <DataTable
                isLined
                data-testid="crowdstrike-nic-history-table"
                pageSize={nicPageSize}
                autoResetPage={false}
                columns={dataTableInterfaceColumns}
                autoResetSortBy={false}
                isPaginationManual={true}
                onFetchData={nicOnFetchData}
                initialState={{
                  pageIndex: nicPageIndex,
                  sortBy: nicSortBy,
                  pageSize: nicPageSize,
                }}
                {...nicPaginationProps}
                emptyBodyContent={
                  <Center py={8}>
                    <Text color="gray.400">
                      No interface history data available
                    </Text>
                  </Center>
                }
              />
            </FlushPanelContent>
          </Panel>
        </VStack>
      )}
    </VStack>
  );
};
